/**
 * InlineBanner — the row/field-level notice (see DESIGN.md "Inline banners").
 *
 * Banner's smaller sibling: same variant semantics, same "the variant fixes
 * the aria role so it cannot drift" rule, no box. It is one line of text
 * hanging off the control it is about, where a bordered, tinted strip would
 * be louder than the field.
 *
 * Placement, not margin classes, is the caller's only spacing choice.
 */
import type { ReactNode } from "react";

import styles from "./InlineBanner.module.css";

export type InlineBannerVariant = "warning" | "error";

/** Where the notice sits — see InlineBanner.module.css for each. */
type Placement = "field" | "none";

export function InlineBanner({
  variant,
  placement = "field",
  children,
  className,
  "data-testid": testId,
}: {
  variant: InlineBannerVariant;
  placement?: Placement;
  children: ReactNode;
  /**
   * Layout only — never a colour or a margin. Two module classes on one
   * element resolve by the stylesheet's own source order, not by intent, so
   * a caller that passes a colour is not overriding the variant, it is
   * gambling on which rule came first.
   */
  className?: string;
  "data-testid"?: string;
}) {
  return (
    <p
      // error announces assertively; warning announces politely, matching
      // Banner exactly.
      role={variant === "error" ? "alert" : "status"}
      // The marker scrollToFirstBlockingNotice finds a blocked action's first
      // error by. It is on every variant so the attribute means "an inline
      // banner of this kind", not "a scroll target" — nothing else may key
      // behaviour off the warning.
      data-inline-banner={variant}
      data-testid={testId}
      className={`${styles.inlineBanner} ${styles[variant]} ${
        styles[placement]
      }${className ? ` ${className}` : ""}`}
    >
      {children}
    </p>
  );
}
